import { create } from 'zustand'

interface State {
  /** True while a 429 block window is active. */
  rateLimited: boolean
  /** ISO of the most recent 429. */
  rateLimitedAt: string | null
  /** ISO when the block lifts (UTC midnight for Cloudflare quota;
   *  ratelimit-reset header time for transient bsky limits). */
  resumesAt: string | null
  /** Human-readable reason shown in the UI banner. */
  rateLimitReason: string | null
  /** True if `fetch` itself threw (network down / DNS / CORS). */
  networkDown: boolean
  /** Last error message surfaced. */
  lastError: string | null
  /** reportRateLimited: call with clearAfterMs for transient bsky
   *  limits (auto-clears after the window). Omit for Cloudflare daily
   *  quota (clears at UTC midnight via auto-timer). */
  reportRateLimited: (clearAfterMs?: number) => void
  reportNetworkDown: (msg: string) => void
  clear: () => void
}

function nextUtcMidnightIso(): string {
  const d = new Date()
  d.setUTCHours(24, 0, 0, 0)
  return d.toISOString()
}

export const useApiHealth = create<State>((set, get) => ({
  rateLimited: false,
  rateLimitedAt: null,
  resumesAt: null,
  rateLimitReason: null,
  networkDown: false,
  lastError: null,
  reportRateLimited: (clearAfterMs?: number) => {
    const resumesAt = clearAfterMs
      ? new Date(Date.now() + clearAfterMs).toISOString()
      : nextUtcMidnightIso()
    const reason = clearAfterMs
      ? `Bluesky rate limit — resumes in ${Math.ceil(clearAfterMs / 1000)}s`
      : 'Cloudflare daily quota exhausted — resumes at UTC midnight'
    set({
      rateLimited: true,
      rateLimitedAt: new Date().toISOString(),
      resumesAt,
      rateLimitReason: reason,
      networkDown: false,
      lastError: reason,
    })
    // Auto-clear when the window elapses — no page refresh needed.
    const waitMs = clearAfterMs ?? (new Date(resumesAt).getTime() - Date.now())
    setTimeout(() => {
      if (get().rateLimited) get().clear()
    }, Math.max(waitMs, 0))
  },
  reportNetworkDown: (msg) =>
    set({
      networkDown: true,
      lastError: msg,
    }),
  clear: () =>
    set({
      rateLimited: false,
      rateLimitedAt: null,
      resumesAt: null,
      rateLimitReason: null,
      networkDown: false,
      lastError: null,
    }),
}))

/** Fast read for poll loops — they should bail when this is true. */
export function isApiBlocked(): boolean {
  const s = useApiHealth.getState()
  return s.rateLimited || s.networkDown
}
